// Vignette d'un play (D-043) : un temps de l'animation (format pb-play/2 de
// l'éditeur), dessiné en SVG léger — terrain, attaquantes numérotées, défenseuses,
// déplacements et passes. Mêmes coordonnées que le moteur (demi-terrain 174 × 164,
// plein terrain 174 × 304). Le SVG ne contient que des nombres : sûr à insérer.
// Un exercice (atelier) : la toile inclut les extensions (10 unités par mètre) et
// les joueuses portent la couleur de leur chasuble.

type Pt = { x: number; y: number };
interface Step {
  positions?: Record<string, Pt>;
  moves?: { player: number | string; to: Pt; via?: Pt[] }[];
  passes?: { from: number; to: number }[];
  /** Les variantes qui partent de ce temps (moteur, jalon 4) : désactivables par le coach. */
  branches?: { id?: unknown; name?: unknown; enabled?: unknown; steps?: unknown[] }[];
}
interface Player { id?: unknown; n?: unknown; side?: unknown; bib_color?: unknown }
type Ext = { top?: unknown; left?: unknown; right?: unknown; bottom?: unknown };
export interface CourtAnim { court_type?: string; court_base?: string; extensions?: Ext; terrain_config?: { extensions?: Ext }; players?: unknown[]; steps?: unknown[];
  /** La défense est-elle montrée ? Comme le moteur : absente = non (sauf un atelier, où chacun est à sa place). */
  defense_enabled?: boolean; defenseEnabled?: boolean }

// Les couleurs de chasubles du moteur (BIBS), et l'encre lisible dessus.
const BIB: Record<string, [string, string]> = {
  red: ['#EF4444', '#FFFFFF'], blue: ['#3B82F6', '#FFFFFF'], yellow: ['#FACC15', '#111827'],
  green: ['#22C55E', '#FFFFFF'], orange: ['#F97316', '#FFFFFF'], purple: ['#A855F7', '#FFFFFF'],
};
const NEUTRAL: [string, string] = ['#6B7280', '#FFFFFF'];
const meters = (v: unknown) => (typeof v === 'number' && isFinite(v) ? Math.max(0, Math.min(20, v)) * 10 : 0);

const n = (v: number) => Math.round(v * 10) / 10;
const ok = (p: unknown): p is Pt => !!p && typeof (p as Pt).x === 'number' && typeof (p as Pt).y === 'number' && isFinite((p as Pt).x) && isFinite((p as Pt).y);

const HALF = '<rect class="ct-floor" x="0" y="0" width="174" height="164"/>'
  + '<rect class="ct-ln" x="12" y="12" width="150" height="140"/>'
  + '<rect class="ct-ln" x="62.5" y="12" width="49" height="58"/>'
  + '<circle class="ct-ln" cx="87" cy="70" r="18"/>'
  + '<path class="ct-ln" d="M 21 12 L 21 27.75 A 67.5 67.5 0 0 0 153 27.75 L 153 12"/>'
  + '<path class="ct-ln" d="M 69 152 A 18 18 0 0 1 105 152"/>'
  + '<circle class="ct-ln" cx="87" cy="27.75" r="2.6"/>';

const FULL = '<rect class="ct-floor" x="0" y="0" width="174" height="304"/>'
  + '<rect class="ct-ln" x="12" y="12" width="150" height="280"/>'
  + '<line class="ct-ln" x1="12" y1="152" x2="162" y2="152"/>'
  + '<circle class="ct-ln" cx="87" cy="152" r="18"/>'
  + '<rect class="ct-ln" x="62.5" y="12" width="49" height="58"/>'
  + '<rect class="ct-ln" x="62.5" y="234" width="49" height="58"/>'
  + '<path class="ct-ln" d="M 21 12 L 21 27.75 A 67.5 67.5 0 0 0 153 27.75 L 153 12"/>'
  + '<path class="ct-ln" d="M 21 292 L 21 276.25 A 67.5 67.5 0 0 1 153 276.25 L 153 292"/>'
  + '<circle class="ct-ln" cx="87" cy="27.75" r="2.6"/><circle class="ct-ln" cx="87" cy="276.25" r="2.6"/>';

/** Nombre de temps d'une animation (ligne principale). */
export const stepCount = (a: CourtAnim | null | undefined) => (Array.isArray(a?.steps) ? a!.steps!.length : 0);

/** Un temps à montrer : de la ligne principale (`branch` null) ou d'une variante, avec sa place dans sa ligne. */
export interface Frame { step: Step; branch: string | null; index: number; count: number }

/** Les temps d'une animation à la suite : la ligne principale, puis chaque variante active dans l'ordre où elle
 *  part (ses sous-variantes juste après elle). Une variante désactivée par le coach n'est pas montrée, comme côté joueuse. */
export function playFrames(anim: CourtAnim | null | undefined): Frame[] {
  const out: Frame[] = [];
  const line = (steps: unknown[], branch: string | null, depth: number) => {
    const list = steps.filter(s => !!s && typeof s === 'object') as Step[];
    list.forEach((step, index) => out.push({ step, branch, index, count: list.length }));
    if (depth >= 3) return;
    for (const st of list) {
      for (const b of Array.isArray(st.branches) ? st.branches : []) {
        if (!b || b.enabled === false || !Array.isArray(b.steps) || !b.steps.length) continue;
        const name = typeof b.name === 'string' && b.name.trim() ? b.name.trim().slice(0, 40) : 'Variante';
        line(b.steps, branch ? `${branch} › ${name}` : name, depth + 1);
      }
    }
  };
  line(Array.isArray(anim?.steps) ? anim!.steps! : [], null, 0);
  return out;
}

/** Le temps `i` (0 = départ) d'une animation, en SVG ; null si rien à dessiner. */
export function stepSvg(anim: CourtAnim | null | undefined, i = 0, fit: 'meet' | 'slice' = 'meet'): string | null {
  const steps = Array.isArray(anim?.steps) ? (anim!.steps as Step[]) : [];
  return stepToSvg(anim, steps[Math.max(0, Math.min(i, steps.length - 1))], fit);
}

/** Un temps donné (de `playFrames`), en SVG. */
export const frameSvg = (anim: CourtAnim | null | undefined, f: Frame | undefined, fit: 'meet' | 'slice' = 'meet') => stepToSvg(anim, f?.step, fit);

function stepToSvg(anim: CourtAnim | null | undefined, st: Step | undefined, fit: 'meet' | 'slice'): string | null {
  if (!st || !st.positions || typeof st.positions !== 'object') return null;
  const training = anim?.court_type === 'training';
  // La défense n'est dessinée que si le coach l'a mise (comme le moteur) ; un atelier montre tout le monde.
  const defOn = training || anim?.defense_enabled === true || anim?.defenseEnabled === true;
  const full = anim?.court_type === 'full' || (training && anim?.court_base === 'full');
  const pos = st.positions;
  const ext = training ? (anim?.extensions ?? anim?.terrain_config?.extensions ?? {}) : {};
  const [el, er, et, eb] = [meters(ext.left), meters(ext.right), meters(ext.top), meters(ext.bottom)];
  const w = 174 + el + er, h = (full ? 304 : 164) + et + eb;
  // Les joueuses d'un atelier (côté « neu ») : numéro et chasuble, par identifiant.
  const neutral = new Map<string, { n: string; bib: [string, string] }>();
  const roster = new Set<string>();   // les joueuses du document : une position sans joueuse est un reste, pas un jeton
  for (const p of (Array.isArray(anim?.players) ? anim!.players : []) as Player[]) {
    if (p && typeof p.id === 'string') roster.add(p.id);
    if (!p || typeof p.id !== 'string' || p.side !== 'neu') continue;
    neutral.set(p.id, { n: typeof p.n === 'number' ? String(Math.trunc(p.n)).slice(0, 2) : '', bib: BIB[String(p.bib_color)] ?? NEUTRAL });
  }
  const neutralId = (num: number) => [...neutral].find(([, v]) => v.n === String(num - 100))?.[0] ?? `n${num - 100}`;
  // Comme le moteur (refId) : un numéro positif = attaquante, négatif = défenseuse, 101… = joueuse d'atelier ;
  // ou l'identifiant lui-même.
  const key = (p: number | string) => (typeof p === 'string' ? p : p > 100 ? neutralId(p) : p < 0 ? `d${-p}` : `a${p}`);
  const shown = (k: string) => (defOn || !/^d\d+$/.test(k)) && (!training || !roster.size || roster.has(k));
  const parts: string[] = [];
  if (el || er || et || eb) parts.push(`<rect class="ct-floor ct-ext" x="0" y="0" width="${w}" height="${h}"/>`);
  parts.push(el || et ? `<g transform="translate(${el} ${et})">${full ? FULL : HALF}</g>` : full ? FULL : HALF);
  for (const pa of Array.isArray(st.passes) ? st.passes : []) {
    const a = pos[key(pa.from)], b = pos[key(pa.to)];
    if (ok(a) && ok(b) && shown(key(pa.from)) && shown(key(pa.to))) parts.push(`<line class="ct-pass" x1="${n(a.x)}" y1="${n(a.y)}" x2="${n(b.x)}" y2="${n(b.y)}"/>`);
  }
  for (const mv of Array.isArray(st.moves) ? st.moves : []) {
    const from = pos[key(mv.player)];
    if (!ok(from) || !ok(mv.to) || !shown(key(mv.player))) continue;
    const pts = [from, ...(Array.isArray(mv.via) ? mv.via.filter(ok) : []), mv.to];
    parts.push(`<polyline class="ct-move" points="${pts.map(p => `${n(p.x)},${n(p.y)}`).join(' ')}"/>`);
    parts.push(`<circle class="ct-head" cx="${n(mv.to.x)}" cy="${n(mv.to.y)}" r="2.2"/>`);
  }
  for (const [k, p] of Object.entries(pos)) {
    if (!ok(p) || !shown(k) || neutral.has(k) || !/^d\d+$/.test(k)) continue;
    parts.push(`<circle class="ct-d" cx="${n(p.x)}" cy="${n(p.y)}" r="5"/>`);
  }
  for (const [k, p] of Object.entries(pos)) {
    const nt = neutral.get(k);
    if (!ok(p) || !nt) continue;
    parts.push(`<circle class="ct-b" cx="${n(p.x)}" cy="${n(p.y)}" r="6.5" fill="${nt.bib[0]}"/>`
      + (nt.n ? `<text class="ct-n" x="${n(p.x)}" y="${n(p.y + 2.6)}" text-anchor="middle" fill="${nt.bib[1]}">${nt.n}</text>` : ''));
  }
  for (const [k, p] of Object.entries(pos)) {
    if (!ok(p) || !shown(k) || neutral.has(k) || /^d\d+$/.test(k)) continue;
    const num = (k.match(/\d+/)?.[0] ?? '').slice(0, 2);
    parts.push(`<circle class="ct-a" cx="${n(p.x)}" cy="${n(p.y)}" r="6.5"/>`
      + (num ? `<text class="ct-n" x="${n(p.x)}" y="${n(p.y + 2.6)}" text-anchor="middle">${num}</text>` : ''));
  }
  return `<svg class="court-svg" viewBox="0 0 ${w} ${h}" preserveAspectRatio="${fit === 'slice' ? 'xMidYMin slice' : 'xMidYMid meet'}" aria-hidden="true">${parts.join('')}</svg>`;
}
